import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Receipt, X, Upload, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from './formatters';

export function ReceiptsSection({ teachers, onImport }) {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalAddebiti, setTotalAddebiti] = useState(0);
  const [totalAltro, setTotalAltro] = useState(0);
  const [totalPagato, setTotalPagato] = useState(0);
  const [filterTeacherId, setFilterTeacherId] = useState('');
  const [filterAnnoCompetenza, setFilterAnnoCompetenza] = useState('');
  const [availableAnniCompetenza, setAvailableAnniCompetenza] = useState([]);
  // Inline editing state: {rowId, field} and current value
  const [editing, setEditing] = useState({ id: null, field: null });
  const [editingValue, setEditingValue] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterTeacherId) params.teacher_id = filterTeacherId;
      if (filterAnnoCompetenza) params.anno_competenza = filterAnnoCompetenza;
      const response = await api.getAdminReceipts(params);
      setReceipts(response.data.records);
      setTotalAddebiti(response.data.total_amount || 0);
      setTotalAltro(response.data.total_altro || 0);
      setTotalPagato(response.data.total_pagato || 0);
      if (response.data.available_anni_competenza) setAvailableAnniCompetenza(response.data.available_anni_competenza);
    } catch { toast.error('Errore nel caricamento ricevute'); }
    finally { setLoading(false); }
  }, [filterTeacherId, filterAnnoCompetenza]);

  useEffect(() => { load(); }, [load]);

  const hasFilters = filterTeacherId || filterAnnoCompetenza;
  const clearFilters = () => { setFilterTeacherId(''); setFilterAnnoCompetenza(''); };

  const beginEdit = (r, field) => {
    setEditing({ id: r.id, field });
    setEditingValue(String(r[field] ?? ''));
  };

  const cancelEdit = () => { setEditing({ id: null, field: null }); setEditingValue(''); };

  const commitEdit = async () => {
    if (!editing.id || !editing.field) return;
    const field = editing.field;
    const id = editing.id;
    try {
      if (field === 'altro') {
        const parsed = parseFloat(String(editingValue).replace(',', '.'));
        const altro = isNaN(parsed) ? 0 : parsed;
        await api.updateReceiptAltro(id, altro);
      } else if (field === 'totale') {
        const parsed = parseFloat(String(editingValue).replace(',', '.'));
        const totale = isNaN(parsed) ? 0 : parsed;
        await api.updateReceiptTotale(id, totale);
      } else {
        // nr_ricevuta or nr_fattura
        await api.updateReceiptDoc(id, { [field]: editingValue });
      }
      toast.success('Aggiornato');
      await load();
    } catch { toast.error('Errore aggiornamento'); }
    finally { cancelEdit(); }
  };

  const changeTipoDocumento = async (r, newTipo) => {
    try {
      await api.updateReceiptDoc(r.id, { tipo_documento: newTipo });
      toast.success('Tipo documento aggiornato');
      await load();
    } catch { toast.error('Errore aggiornamento'); }
  };

  const handleDelete = async (r) => {
    const label = `${r.tipo_documento === 'fattura' ? 'fattura' : 'ricevuta'} nr. ${r.nr_ricevuta || '-'}`;
    const ok = window.confirm(`Eliminare definitivamente la ${label} di ${(r.teacher_name || '').toUpperCase()}?\n\nGli addebiti collegati verranno scollegati (nr. ricevuta e importo pagato azzerati).`);
    if (!ok) return;
    try {
      const resp = await api.deleteReceipt(r.id);
      const unlinked = resp.data?.unlinked_payments || 0;
      toast.success(`Ricevuta eliminata${unlinked ? ` · ${unlinked} ${unlinked === 1 ? 'addebito scollegato' : 'addebiti scollegati'}` : ''}`);
      await load();
    } catch { toast.error('Errore eliminazione'); }
  };

  const isEditing = (r, field) => editing.id === r.id && editing.field === field;

  return (
    <>
      <div className="mb-8 animate-fade-in flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Gestione Ricevute</h1>
          <p className="text-[#52525B] text-sm">Visualizza tutte le ricevute emesse, filtra per insegnante</p>
        </div>
        {onImport && (
          <Button onClick={onImport} className="bg-[#0A0A0A] rounded-sm" data-testid="receipts-import-btn">
            <Upload size={16} className="mr-2" />Importa Ricevute
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6 animate-fade-in animate-delay-1">
        <div className="bg-white border border-[#E4E4E7] rounded-sm p-4 text-center" data-testid="admin-receipts-count">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#52525B] mb-1">Ricevute Emesse</div>
          <div className="text-2xl font-bold font-['Work_Sans']">{receipts.length}</div>
        </div>
        <div className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-sm p-4 text-center" data-testid="admin-receipts-total-addebiti">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#4338CA] mb-1">Totale Addebiti</div>
          <div className="text-2xl font-bold font-['Work_Sans'] text-[#4338CA]">{formatCurrency(totalAddebiti)}</div>
        </div>
        <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-sm p-4 text-center" data-testid="admin-receipts-total-altro">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#92400E] mb-1">Totale Altro</div>
          <div className="text-2xl font-bold font-['Work_Sans'] text-[#92400E]">{formatCurrency(totalAltro)}</div>
        </div>
        <div className="bg-[#D1FAE5] border border-[#6EE7B7] rounded-sm p-4 text-center" data-testid="admin-receipts-total-pagato">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#065F46] mb-1">Totale Pagato</div>
          <div className="text-2xl font-bold font-['Work_Sans'] text-[#065F46]">{formatCurrency(totalPagato)}</div>
        </div>
      </div>
      <div className="flex items-center gap-3 mb-4 flex-wrap animate-fade-in animate-delay-2">
        <Select value={filterTeacherId} onValueChange={setFilterTeacherId}>
          <SelectTrigger className={`w-full sm:w-[250px] rounded-sm h-10 text-sm font-semibold border-2 ${filterTeacherId ? 'border-[#2D2A4A] bg-[#2D2A4A] text-white' : 'border-[#2D2A4A] text-[#2D2A4A]'}`} data-testid="filter-receipts-teacher"><SelectValue placeholder="Tutti gli insegnanti" /></SelectTrigger>
          <SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name?.toUpperCase()}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filterAnnoCompetenza} onValueChange={setFilterAnnoCompetenza}>
          <SelectTrigger className="w-full sm:w-[160px] rounded-sm h-9 text-sm" data-testid="filter-receipts-anno-competenza"><SelectValue placeholder="Anno Competenza" /></SelectTrigger>
          <SelectContent>{availableAnniCompetenza.map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
        </Select>
        {hasFilters && <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-sm text-[#52525B]" data-testid="clear-receipts-filter"><X size={14} className="mr-1" />Pulisci</Button>}
        <div className="text-sm text-[#52525B] font-medium ml-auto" data-testid="admin-receipts-count-label">{receipts.length} {receipts.length === 1 ? 'ricevuta' : 'ricevute'}</div>
      </div>
      <div className="animate-fade-in animate-delay-3 overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        {loading ? (
          <div className="text-center py-12 text-[#52525B]">Caricamento...</div>
        ) : receipts.length === 0 ? (
          <div className="text-center py-12 bg-white border border-[#E4E4E7] rounded-sm"><Receipt size={48} className="mx-auto text-[#E4E4E7] mb-4" /><p className="text-[#52525B]">{hasFilters ? 'Nessuna ricevuta trovata' : 'Nessuna ricevuta importata. Usa "Importa Ricevute" per iniziare.'}</p></div>
        ) : (
          <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
            <table className="w-full" data-testid="admin-receipts-table">
              <thead><tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Insegnante</th>
                <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Anno Comp.</th>
                <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Documento</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Nr. Ricevuta</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Nr. Fattura</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Data</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Totale Addebiti</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#92400E]">Altro</th>
                <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#065F46]">Totale Pagato</th>
                <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B] w-16">Azioni</th>
              </tr></thead>
              <tbody>
                {receipts.map(r => (
                  <tr key={r.id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]" data-testid={`admin-receipt-row-${r.id}`}>
                    <td className="px-3 py-1.5 text-sm uppercase">{r.teacher_name || '-'}</td>
                    <td className="px-3 py-1.5 text-sm text-center font-['Work_Sans']">{r.anno_competenza || '-'}</td>
                    {/* Tipo documento */}
                    <td className="px-3 py-1.5 text-sm text-center">
                      <select
                        value={r.tipo_documento || 'ricevuta'}
                        onChange={(e) => changeTipoDocumento(r, e.target.value)}
                        className="text-xs font-semibold uppercase border border-[#E4E4E7] rounded px-1.5 py-0.5 bg-white cursor-pointer hover:border-[#2D2A4A]"
                        data-testid={`receipt-tipo-${r.id}`}
                      >
                        <option value="ricevuta">Ricevuta</option>
                        <option value="fattura">Fattura</option>
                      </select>
                    </td>
                    {/* Nr Ricevuta editable */}
                    <td className="px-3 py-1.5 text-sm font-medium">
                      {isEditing(r, 'nr_ricevuta') ? (
                        <input
                          type="text"
                          autoFocus
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={commitEdit}
                          onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') cancelEdit(); }}
                          className="w-28 h-7 border border-[#2D2A4A] rounded px-1 text-sm font-medium"
                          data-testid={`receipt-nr-ricevuta-input-${r.id}`}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => beginEdit(r, 'nr_ricevuta')}
                          className="px-1 py-0.5 rounded hover:bg-[#EEF2FF] font-medium cursor-pointer"
                          title="Clicca per modificare"
                          data-testid={`receipt-nr-ricevuta-${r.id}`}
                        >
                          {r.nr_ricevuta || '-'}
                        </button>
                      )}
                    </td>
                    {/* Nr Fattura editable */}
                    <td className="px-3 py-1.5 text-sm">
                      {isEditing(r, 'nr_fattura') ? (
                        <input
                          type="text"
                          autoFocus
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={commitEdit}
                          onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') cancelEdit(); }}
                          className="w-28 h-7 border border-[#2D2A4A] rounded px-1 text-sm"
                          data-testid={`receipt-nr-fattura-input-${r.id}`}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => beginEdit(r, 'nr_fattura')}
                          className="px-1 py-0.5 rounded hover:bg-[#EEF2FF] cursor-pointer text-[#52525B]"
                          title="Clicca per modificare"
                          data-testid={`receipt-nr-fattura-${r.id}`}
                        >
                          {r.nr_fattura || '-'}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-sm text-[#52525B]">{formatDate(r.data_ricevuta)}</td>
                    <td className="px-3 py-1.5 text-sm text-right font-['Work_Sans']">
                      {isEditing(r, 'totale') ? (
                        <input
                          type="number"
                          step="0.01"
                          autoFocus
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={commitEdit}
                          onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') cancelEdit(); }}
                          className="w-24 h-7 text-right border border-[#4338CA] rounded px-1 text-sm font-['Work_Sans']"
                          data-testid={`receipt-totale-input-${r.id}`}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => beginEdit(r, 'totale')}
                          className="px-1 py-0.5 rounded hover:bg-[#EEF2FF] text-right font-semibold cursor-pointer"
                          title="Clicca per modificare"
                          data-testid={`receipt-totale-${r.id}`}
                        >
                          {formatCurrency(r.totale)}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-sm text-right font-['Work_Sans']">
                      {isEditing(r, 'altro') ? (
                        <input
                          type="number"
                          step="0.01"
                          autoFocus
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={commitEdit}
                          onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') cancelEdit(); }}
                          className="w-24 h-7 text-right border border-[#F59E0B] rounded px-1 text-sm font-['Work_Sans']"
                          data-testid={`receipt-altro-input-${r.id}`}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => beginEdit(r, 'altro')}
                          className="px-1 py-0.5 rounded hover:bg-[#FEF3C7] text-right font-semibold text-[#92400E] cursor-pointer"
                          title="Clicca per modificare"
                          data-testid={`receipt-altro-${r.id}`}
                        >
                          {formatCurrency(r.altro || 0)}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-sm text-right font-bold font-['Work_Sans'] text-[#065F46]" data-testid={`receipt-totale-pagato-${r.id}`}>{formatCurrency(r.totale_pagato)}</td>
                    <td className="px-3 py-1.5 text-center">
                      <button
                        type="button"
                        onClick={() => handleDelete(r)}
                        className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-red-50 text-red-600 hover:text-red-700 border border-transparent hover:border-red-200"
                        title="Elimina ricevuta"
                        data-testid={`receipt-delete-${r.id}`}
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    </td>
                  </tr>
                ))}
                <tr className="bg-[#FAFAFA] border-t-2 border-[#E4E4E7]">
                  <td colSpan={6} className="px-3 py-2 text-sm font-bold uppercase">Totale</td>
                  <td className="px-3 py-2 text-sm text-right font-bold font-['Work_Sans']">{formatCurrency(totalAddebiti)}</td>
                  <td className="px-3 py-2 text-sm text-right font-bold font-['Work_Sans'] text-[#92400E]">{formatCurrency(totalAltro)}</td>
                  <td className="px-3 py-2 text-sm text-right font-bold font-['Work_Sans'] text-[#065F46]">{formatCurrency(totalPagato)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
